import clsx from "clsx";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Warning } from "@/assets/icons/ds";
import { Toggle } from "@/components/Interface/Toggle/Toggle";

interface Props {
  checked: boolean;
  title: ReactNode;
  subtitle: ReactNode;
  /**
   * The setting exists but cannot be switched right now (e.g. adult
   * entertainment while value-added SMS are off). The design replaces the
   * switch with a warning sign and dims the whole row.
   */
  blocked?: boolean;
  /** Read-only outside edit mode: the switch still shows the state, greyed out. */
  readOnly?: boolean;
  /**
   * Name for the switch when `title` is not plain text. A card full of these
   * rows otherwise gives every switch the same name.
   */
  ariaLabel?: string;
  onToggle: () => void;
  dataTestid?: string;
}

/**
 * One row of the call settings: what the setting does on the left, its switch
 * on the right.
 */
export const SettingToggle: FC<Props> = ({
  checked,
  title,
  subtitle,
  blocked,
  readOnly,
  ariaLabel,
  onToggle,
  dataTestid,
}) => {
  const { t } = useTranslation("portal");

  return (
    <div
      data-testid={dataTestid}
      className="flex w-full items-start gap-2 overflow-hidden rounded-md bg-surface-quaternary p-4"
    >
      <div
        className={clsx(
          "flex min-w-0 flex-1 flex-col justify-center gap-1",
          blocked ? "text-foreground-tertiary" : "text-foreground-primary",
        )}
      >
        {/* Both lines wrap rather than truncate: the design gives "Roaming and
          international calls" two lines inside the 336px card. */}
        <p className="text-body-sm font-semibold leading-none">{title}</p>
        <p
          className={clsx(
            "text-body-xs leading-none",
            !blocked && "text-foreground-secondary",
          )}
        >
          {subtitle}
        </p>
      </div>
      {blocked ? (
        // Named, so the blocked state is not conveyed by the icon alone.
        <Warning
          className="size-6 shrink-0 text-foreground-tertiary"
          role="img"
          aria-hidden={false}
          aria-label={t("common.status.notAvailable")}
        />
      ) : (
        <Toggle
          size="sm"
          ariaLabel={
            ariaLabel ?? (typeof title === "string" ? title : undefined)
          }
          enabled={checked}
          disabled={readOnly}
          onChange={onToggle}
        />
      )}
    </div>
  );
};
